import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { cartContext } from "../../Context/CartContext";
import { useNavigate } from "react-router-dom";
import { Check, X, RefreshCw, Info } from "lucide-react";

const Checkout = () => {
  const navigate = useNavigate();
  const [cartDetails, setCartDetails] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    recoveryNumber: "",
    streetAddress: "",
    city: "",
    zipCode: "",
    notes: "",
  });

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [cities, setCities] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [discountStatus, setDiscountStatus] = useState({
    loading: false,
    applied: false,
    error: null,
  });
  const { getLoggedUserCart } = useContext(cartContext);

  const applyDiscount = async () => {
    setDiscountStatus({ loading: true, applied: false, error: null });

    try {
      const response = await axios.post(
        "https://api.elza2.store/api/v1/coupon/apply",
        { code: discountCode },
        { headers: { Authorization: localStorage.getItem("Auth") } }
      );

      // Log the response for debugging

      if (response?.data?.message === "success") {
        toast.success("Discount applied successfully!");
        setCartDetails(response.data.cart); // Update to access the correct structure
        setDiscountStatus({
          loading: false,
          applied: true,
          error: null,
        });
      } else {
        setDiscountStatus({
          loading: false,
          applied: false,
          error: response?.data?.message || "Invalid discount code",
        });
      }
    } catch (error) {
      // Log the error for debugging
      console.error("Error applying discount:", error);

      setDiscountStatus({
        loading: false,
        applied: false,
        error:
          error?.response?.data?.message ||
          "Failed to apply discount. Please try again.",
      });
      toast.error("Failed to apply discount");
    }
  };

  useEffect(() => {
    const fetchCartDetails = async () => {
      try {
        const response = await getLoggedUserCart();
        setCartDetails(response.data.response.cart);
        setLoading(false);
      } catch (error) {
        toast.error("Failed to fetch cart details");
        setLoading(false);
      }
    };

    const fetchCities = async () => {
      try {
        const response = await axios.get(
          "https://api.elza2.store/api/v1/governates"
        );
        setCities(response.data.governorates);
      } catch (error) {
        toast.error("Failed to fetch cities");
      }
    };

    fetchCartDetails();
    fetchCities();
  }, [getLoggedUserCart]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    validateField(name, value);

    if (name === "city") {
      const selectedCity = cities.find(
        (city) => city.governorate_name_en === value
      );
      if (selectedCity) {
        setShippingCost(selectedCity.shippingCost);
      } else {
        setShippingCost(0);
      }
    }
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "firstName":
      case "lastName":
      case "streetAddress":
        if (!value.trim()) {
          error = "This field is required.";
        }
        break;
      case "phone":
        if (!value.trim()) {
          error = "This field is required.";
        } else if (!/^\d{11}$/.test(value)) {
          error = "Phone number must be exactly 11 digits.";
        }
        break;
      case "city":
        if (!value.trim()) {
          error = "City is required.";
        }
        break;
      case "zipCode":
        if (!/^\d{5}$/.test(value)) {
          error = "ZIP code must be 5 digits.";
        }
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      validateField(key, formData[key]);
      if (errors[key]) {
        newErrors[key] = errors[key];
      }
    });

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      toast.error("Please fix the form errors before proceeding", {
        duration: 2000,
      });
      return;
    }

    try {
      const headers = { Authorization: localStorage.getItem("Auth") };
      const response = await axios.post(
        `https://api.elza2.store/api/v1/order/${cartDetails._id}`,
        formData,
        { headers }
      );

      if (response.status === 200) {
        toast.success("Order created successfully");
        navigate("/order-details/" + response.data.order._id);
      } else {
        throw new Error("Failed to place order");
      }
    } catch (error) {
      toast.error("Failed to place order. Please try again.");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const totalPriceBeforeDiscount = cartDetails?.summary?.totalPrice || 0;
  const totalPriceAfterDiscount =
    cartDetails?.summary?.totalAfterDiscount || totalPriceBeforeDiscount;
  const totalPriceAfterShipping = totalPriceAfterDiscount + shippingCost;

  return (
    <div className="w-full max-w-[85rem] py-10 px-4 sm:px-6 lg:px-8 mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
        Checkout
      </h1>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-12 gap-4">
          <div className="md:col-span-12 lg:col-span-8 col-span-12">
            <div className="bg-white rounded-xl shadow p-4 sm:p-7 dark:bg-slate-900">
              <div className="mb-6">
                <h2 className="text-xl font-bold underline text-gray-700 dark:text-white mb-2">
                  Shipping Address
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-gray-700 dark:text-white mb-1"
                      htmlFor="firstName"
                    >
                      First Name
                    </label>
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none ${
                        errors.firstName ? "border-red-500" : ""
                      }`}
                      id="firstName"
                      type="text"
                    />
                    {errors.firstName && (
                      <div className="text-red-500 text-sm">
                        {errors.firstName}
                      </div>
                    )}
                  </div>
                  <div>
                    <label
                      className="block text-gray-700 dark:text-white mb-1"
                      htmlFor="lastName"
                    >
                      Last Name
                    </label>
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none"
                      id="lastName"
                      type="text"
                    />
                    {errors.lastName && (
                      <div className="text-red-500">{errors.lastName}</div>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <label
                    className="block text-gray-700 dark:text-white mb-1"
                    htmlFor="phone"
                  >
                    Phone
                  </label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className={`w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none ${
                      errors.phone ? "border-red-500" : ""
                    }`}
                    id="phone"
                    type="text"
                    maxLength="11"
                  />
                  {errors.phone && (
                    <div className="text-red-500 text-sm">{errors.phone}</div>
                  )}
                </div>
                <div className="mt-4">
                  <label
                    className="block text-gray-700 dark:text-white mb-1"
                    htmlFor="recoveryNumber"
                  >
                    whatsapp Number
                  </label>
                  <input
                    name="recoveryNumber"
                    value={formData.recoveryNumber}
                    onChange={handleChange}
                    className="w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none"
                    id="recoveryNumber"
                    type="text"
                  />
                  {errors.recoveryNumber && (
                    <div className="text-red-500">{errors.recoveryNumber}</div>
                  )}
                </div>{" "}
                <div className="mt-4">
                  <label
                    className="block text-gray-700 dark:text-white mb-1"
                    htmlFor="city"
                  >
                    City
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none ${
                      errors.city ? "border-red-500" : ""
                    }`}
                    id="city"
                  >
                    <option value="">Select a city</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.governorate_name_en}>
                        {city.governorate_name_en} ({city.governorate_name_ar})
                      </option>
                    ))}
                  </select>
                  {errors.city && (
                    <div className="text-red-500">{errors.city}</div>
                  )}
                </div>
                <div className="mt-4">
                  <label
                    className="block text-gray-700 dark:text-white mb-1"
                    htmlFor="streetAddress"
                  >
                    Address
                  </label>
                  <input
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleChange}
                    className="w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none"
                    id="streetAddress"
                    type="text"
                  />
                  {errors.streetAddress && (
                    <div className="text-red-500">{errors.streetAddress}</div>
                  )}
                </div>
                <div className="mt-4">
                  <label
                    className="block text-gray-700 dark:text-white mb-1"
                    htmlFor="zipCode"
                  >
                    ZIP Code
                  </label>
                  <input
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none"
                    id="zipCode"
                    type="text"
                  />
                  {errors.zipCode && (
                    <div className="text-red-500">{errors.zipCode}</div>
                  )}
                </div>
                <div className="mt-4">
                  <label
                    className="block text-gray-700 dark:text-white mb-1"
                    htmlFor="notes"
                  >
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    className="w-full rounded-lg border py-2 px-3 dark:bg-gray-700 dark:text-white dark:border-none"
                    id="notes"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-12 lg:col-span-4 col-span-12">
            <div className="bg-white rounded-xl shadow p-4 sm:p-7 dark:bg-slate-900">
              <div className="text-xl font-bold underline text-gray-700 dark:text-white mb-2">
                BASKET SUMMARY
              </div>
              <ul
                className="divide-y divide-gray-200 dark:divide-gray-700"
                role="list"
              >
                {cartDetails &&
                  cartDetails.items.map((item) => (
                    <li key={item.id} className="py-3 sm:py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <img
                            alt={item.name}
                            className="w-12 h-12 rounded-full"
                            src={item.image}
                          />
                        </div>
                        <div className="flex-1 min-w-0 ms-4">
                          <p className="text-sm font-medium text-gray-900 truncate dark:text-white">
                            {item.name}
                          </p>
                          <p className="text-sm text-gray-500 truncate dark:text-gray-400">
                            Quantity: {item.quantity}
                          </p>
                        </div>
                        <div className="inline-flex items-center text-base font-semibold text-gray-900 dark:text-white">
                          {item?.totalPrice?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "EGP",
                          })}
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow p-4 sm:p-7 mt-2 dark:bg-slate-900">
              <div className="text-xl font-bold underline text-gray-700 dark:text-white mb-2">
                DISCOUNT CODE
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    placeholder="Enter discount code"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value);
                      setDiscountStatus({
                        loading: false,
                        applied: false,
                        error: null,
                      });
                    }}
                    className={`w-full rounded-lg border py-2 px-3 pr-10 
                    ${
                      discountStatus.applied
                        ? "border-green-500"
                        : discountStatus.error
                        ? "border-red-500"
                        : "border-gray-300"
                    }
                    dark:bg-gray-700 dark:text-white dark:border-none`}
                  />
                  {discountStatus.applied && (
                    <Check
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500"
                      size={20}
                    />
                  )}
                  {discountStatus.error && (
                    <X
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500"
                      size={20}
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={applyDiscount}
                  disabled={!discountCode || discountStatus.loading}
                  className={`
                  flex items-center justify-center 
                  rounded-lg py-2 px-4 
                  transition-all duration-300
                  ${
                    !discountCode || discountStatus.loading
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-700 text-white"
                  }
                `}
                >
                  {discountStatus.loading ? (
                    <RefreshCw className="animate-spin" size={20} />
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
              {discountStatus.error && (
                <div className="mt-2 text-red-500 text-sm">
                  {discountStatus.error}
                </div>
              )}
            </div>

            <div className="md:col-span-12 mt-2 lg:col-span-4 col-span-12">
              {/* Promotions Section */}
              {cartDetails?.summary?.promotions &&
                cartDetails.summary.promotions.some(
                  (promo) => promo.message !== "No promotion applied"
                ) && (
                  <div className="bg-white rounded-xl shadow p-4 sm:p-7 dark:bg-slate-900">
                    <div className="text-xl font-bold underline text-gray-700 dark:text-white mb-2">
                      ACTIVE PROMOTIONS
                    </div>
                    {cartDetails.summary.promotions
                      .filter(
                        (promo) => promo.message !== "No promotion applied"
                      ) // Filter out "No promotion applied"
                      .map((promo, index) => (
                        <div
                          key={index}
                          className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-2 flex items-start"
                        >
                          <Info className="mr-3 mt-1 text-blue-500" size={20} />
                          <div>
                            <p className="font-semibold text-blue-800">
                              {promo.category} Promotion
                            </p>
                            <p className="text-sm text-blue-700">
                              {promo.message}
                            </p>
                            <div className="mt-1 text-sm text-blue-600">
                              <span className="line-through mr-2">
                                EGP {promo.actualPrice.toFixed(2)}
                              </span>
                              <span className="font-bold">
                                EGP {promo.paidPrice.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
            </div>

            {/* Detailed Order Summary Section */}
            <div className="bg-white rounded-xl shadow p-4 sm:p-7 mt-2 dark:bg-slate-900">
              <div className="text-xl font-bold underline text-gray-700 dark:text-white mb-2">
                ORDER BREAKDOWN
              </div>

              {/* Item Breakdown */}
              <div className="mb-4">
                <h3 className="font-semibold mb-2 text-gray-700">
                  Items Breakdown
                </h3>
                {cartDetails?.items.map((item) => (
                  <div key={item._id} className="flex justify-between mb-1">
                    <span>
                      {item.name} (x{item.quantity})
                    </span>
                    <span>EGP {item.totalPrice.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <hr className="border-gray-200 my-3" />

              {/* Price Calculation */}
              <div>
                <div className="flex justify-between mb-2">
                  <span>Subtotal</span>
                  <span className="font-semibold">
                    EGP {cartDetails?.summary.totalPrice.toLocaleString()}
                  </span>
                </div>

                {cartDetails?.summary.totalPrice !==
                  cartDetails?.summary.totalAfterDiscount && (
                  <div className="flex justify-between mb-2 text-green-600">
                    <span>Discounts & Promotions</span>
                    <span className="font-semibold">
                      -EGP{" "}
                      {(
                        cartDetails?.summary.totalPrice -
                        cartDetails?.summary.totalAfterDiscount
                      ).toLocaleString()}
                    </span>
                  </div>
                )}

                <div className="flex justify-between mb-2">
                  <span>Shipping Cost</span>
                  <span className="font-semibold">
                    EGP {shippingCost.toLocaleString()}
                  </span>
                </div>

                <hr className="bg-slate-400 my-4 h-0.5 rounded" />

                <div className="flex justify-between mb-4 text-xl font-bold">
                  <span>Total</span>
                  <span>
                    EGP{" "}
                    {(
                      cartDetails?.summary.totalAfterDiscount + shippingCost
                    ).toLocaleString()}
                  </span>
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300 ease-in-out"
                >
                  Place Order
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
